import { Box } from '@mui/material';
import _ from 'lodash';
import querystring from 'query-string';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
	fetchRelatedTopics as fetchRelatedTopicsAction,
	type SearchOptions,
	searchMindItems as searchMindItemsAction,
	searchProducts as searchProductsAction,
	searchTopics as searchTopicsAction,
	searchUnified as searchUnifiedAction,
	searchVideoMindItems as searchVideoMindItemsAction,
} from '../../actions/search.actions';
import { SearchSubmitted } from '../../ampli';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import MasterLayout from '../../components/AppLayout/MasterLayout';
import NotificationAlert, {
	type Alert,
} from '../../components/NotificationAlert/NotificationAlert';
import PageHeader from '../../components/PageHeader/PageHeader';
import ActiveFilterChips from '../../components/Search/ActiveFilterChips/ActiveFilterChips';
import RelatedTopicsPills from '../../components/Search/RelatedTopicsPills/RelatedTopicsPills';
import { SearchFiltersContainer } from '../../components/Search/SearchFiltersContainer';
import SearchResults, {
	type SearchResultsItemsTab,
} from '../../components/Search/SearchResults/SearchResults';
import useSearchQuery from '../../components/utils/useSearchQuery';
import { routes, searchFilterKeys, searchVisibleFilterKeys, zIndex } from '../../constants';
import {
	getPaginationSelector,
	isMindItemPageLoading,
	isProductPageLoading,
	isUnifiedPageLoading,
	isVideoMindItemPageLoading,
	selectSearchFilters,
} from '../../reducers/search.selectors';
import { selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { trackingEvents } from '../../services/tracking/trackConfig';
import { MasterLayoutContext } from '../../state/MasterLayoutProvider';
import { NotificationContext } from '../../state/NotificationContextProvider';
import { type SearchFilters, SearchResultsTab } from '../../types/search';
import { getAmpliSearchEventPayload, startNewSearchJourney } from '../../utils/ampli';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import { getDefaultSearchResultTab } from '../../utils/getDefaultSearchResultTab';
import isCoursePage from '../../utils/isCoursePage';
import queryToSearchFilters from '../../utils/queryToSearchFilters';
import { getRecentSearches, setRecentSearches } from '../../utils/recentSearches';
import {
	getStoredSearchFilters,
	setStoredSearchFilters,
} from '../../utils/searchFiltersLocalStorageManager';
import searchFiltersToQuery from '../../utils/searchFiltersToQuery';
import searchResultsStorageManager from '../../utils/searchResultsStorageManager';
import useAction from '../../utils/useAction';
import useCardDimensions from '../../utils/useCardDimensions';
import useRouter from '../../utils/useRouter';
import useSearchExperiments from '../../utils/useSearchExperiments';
import { SearchPageContainer } from './SearchPageContainer';
import useQueryModification from './useQueryModification';

export type DisabledSearchFilters = {
	[K in keyof SearchFilters]?: boolean;
};

const SearchPage: React.FC = () => {
	const { t } = useTranslation();
	const cardDimensions = useCardDimensions();
	const { router, match } = useRouter();
	const searchMindItems = useAction(searchMindItemsAction);
	const searchVideoMindItems = useAction(searchVideoMindItemsAction);
	const searchProducts = useAction(searchProductsAction);
	const searchUnified = useAction(searchUnifiedAction);
	const searchTopics = useAction(searchTopicsAction);
	const fetchRelatedTopics = useAction(fetchRelatedTopicsAction);

	// Holds filters restored from storage so requestFilters can use them immediately,
	// without waiting for router.replace() to propagate through the found router context.
	const [restoredFilters, setRestoredFilters] = useState<SearchFilters | null>(null);

	// Compute requestFilters from URL so it's reactive and triggers re-renders.
	// Uses restoredFilters when available to bridge the gap before the URL updates.
	const requestFilters = useMemo(() => {
		if (restoredFilters) return restoredFilters;
		return queryToSearchFilters(match.location.search);
	}, [match.location.search, restoredFilters]);
	const mindItemsPagination = useSelector(getPaginationSelector('mindItems'));
	const productsPagination = useSelector(getPaginationSelector('products'));
	const videoMindItemsPagination = useSelector(getPaginationSelector('videoMindItems'));
	const lastSearchFilters = useSelector(selectSearchFilters);
	const user = useSelector(selectUser);
	const {
		isDemoteReadOnlyReady,
		isBestMatchDeepLinkReady,
		isRelatedTopicsReady,
		isUseAiFiltersReady,
		useAiFilters,
		relatedTopicsVariant,
		searchExperiments,
	} = useSearchExperiments();
	const isRelatedTopicsTreatment = isRelatedTopicsReady && relatedTopicsVariant === 'treatment';

	const { notification, hideNotification, showNotification } = useContext(NotificationContext);
	const { mainContentContainer } = useContext(MasterLayoutContext);
	const { updateQueryParams, clearQueryParams } = useQueryModification<SearchFilters>(router, match);
	const { pushSearchQueryToRouter } = useSearchQuery();

	const matchRef = useRef(match);
	matchRef.current = match;

	// Breadcrumb trail of terms visited in the current related-topics drill-down
	// chain. Passed to the related-topics fetch as `excludeTopics` so deeper hops
	// never loop back to an already-visited topic. Held in a ref (not the URL)
	// because a fresh search preserves the existing location query params and
	// would otherwise leak a stale trail across journeys.
	const breadcrumbTrailRef = useRef<string[]>([]);
	// The term last reached via a related-topic chip click. When the active term
	// differs, the user arrived via a fresh search / recent search / deep link,
	// so the trail is reset before the next fetch.
	const lastChipTermRef = useRef<string | null>(null);
	// Latest active term, read inside handleRelatedTopicSelect without making the
	// callback depend on requestFilters (which changes on every filter edit).
	const currentTermRef = useRef<string | undefined>(undefined);
	currentTermRef.current = requestFilters.term;

	const handleRelatedTopicSelect = useCallback(
		(topic: string) => {
			if (user) {
				const searchId = startNewSearchJourney({
					relatedTopicsApplied: true,
					searchSuggestionApplied: false,
				});
				try {
					const currentMatch = matchRef.current;
					const searchPayload = getAmpliSearchEventPayload({
						query: currentMatch.location.query,
						term: topic,
						appSection: isCoursePage(currentMatch.location.pathname) ? 'courses' : 'library',
						searchSuggestionApplied: false,
						relatedTopicsApplied: true,
						searchId,
					});

					if (searchPayload) {
						const event = new SearchSubmitted(searchPayload as any);
						services.track.ampliEventTrack({ event });
					}
				} catch (error) {
					notifyBugsnagHandledError(error as Error);
				}
			}

			// Record the term we're drilling away from as a visited breadcrumb so the
			// next fetch (for `topic`) excludes it — the user can keep diving deeper
			// without the chain looping back to where they came from.
			const parentTerm = currentTermRef.current;
			if (parentTerm && !breadcrumbTrailRef.current.includes(parentTerm)) {
				breadcrumbTrailRef.current = [...breadcrumbTrailRef.current, parentTerm];
			}
			lastChipTermRef.current = topic;

			pushSearchQueryToRouter({ term: topic });
		},
		[user, pushSearchQueryToRouter]
	);

	const removeFilter = useCallback(
		(key: string, value: string) => {
			const currentValues = requestFilters[key as keyof SearchFilters];
			if (typeof currentValues === 'boolean') {
				updateQueryParams({ [key]: undefined } as Partial<SearchFilters>);
			} else if (Array.isArray(currentValues)) {
				const newValues = currentValues.filter((v: string) => v !== value);
				updateQueryParams({
					[key]: newValues.length > 0 ? newValues : undefined,
				} as Partial<SearchFilters>);
			}
		},
		[requestFilters, updateQueryParams]
	);

	const resetAllFilters = useCallback(() => {
		clearQueryParams([...searchFilterKeys]);
	}, [clearQueryParams]);

	const handleShowNotification = (alert: Alert) => {
		showNotification(alert);
	};

	const userId = user && user.id;

	// Tracks whether filter restore from localStorage has been resolved.
	// The main search effect waits for this before triggering searches.
	const [didRestoreFilters, setDidRestoreFilters] = useState(false);

	// On mount: mark restore as resolved (so the search loop can proceed) and,
	// if a userId is available, attempt to restore filters from localStorage.
	// AuthenticatedRoute ensures `user` is loaded before SearchPage mounts in
	// production, so the no-userId branch is a no-op only hit in tests or in
	// edge cases where the auth bootstrap order changes.
	useEffect(() => {
		if (didRestoreFilters) return;

		setDidRestoreFilters(true);

		if (!userId) return;

		const storedFilters = getStoredSearchFilters(userId);
		if (!storedFilters) return;

		const currentQuery = querystring.parse(match.location.search, {
			arrayFormat: 'comma',
		});

		// Deep-link navigation (e.g. Dashboard "Alle anzeigen") puts filter params
		// in the URL intentionally — restoring from localStorage would wipe them.
		// Only restore stored filters when the URL has no filter params of its own.
		const hasUrlFilterParams = Object.keys(currentQuery).some(
			(key) => key !== 'term' && currentQuery[key] != null
		);
		if (hasUrlFilterParams) return;

		const rawTerm = currentQuery.term;
		const term = Array.isArray(rawTerm) ? rawTerm.join(',') : rawTerm;
		const mergedFilters = term ? { ...storedFilters, term } : storedFilters;

		const queryString = new URLSearchParams(searchFiltersToQuery(mergedFilters)).toString();
		if (queryString) {
			setRestoredFilters(mergedFilters);
			router.replace(`${match.location.pathname}?${queryString}`);
		}
	}, [didRestoreFilters, userId, match.location.search, match.location.pathname, router]);

	// Clear restoredFilters once the URL catches up with the restored values.
	useEffect(() => {
		if (restoredFilters && match.location.search) {
			setRestoredFilters(null);
		}
	}, [restoredFilters, match.location.search]);

	// Persist filters to localStorage whenever they change.
	// Uses lastSearchFilters (from Redux) rather than requestFilters to avoid
	// writing empty/default filters before the restore has updated the URL.
	useEffect(() => {
		if (!userId) return;
		if (!didRestoreFilters) return;
		if (!lastSearchFilters) return;
		setStoredSearchFilters(userId, _.omit(lastSearchFilters, 'term'));
	}, [didRestoreFilters, lastSearchFilters, userId]);

	const getCurrentSearchResultsTab = () => {
		const requestedTab = match.params.tab as SearchResultsTab;
		if (!Object.values(SearchResultsTab).includes(requestedTab)) {
			return getDefaultSearchResultTab(user);
		}
		// `ALL` is only rendered for core members (see `getContainerTabs`). For a non-core user
		// deep-linking to /suche/all we'd otherwise sit on a tab whose search action is a no-op,
		// never populating any results.
		if (requestedTab === SearchResultsTab.ALL && !user?.isCoreMember) {
			return getDefaultSearchResultTab(user);
		}
		return requestedTab;
	};

	const currentTab = getCurrentSearchResultsTab();
	const visibleKeys = [...searchVisibleFilterKeys];

	useEffect(() => {
		if (currentTab && Object.values(SearchResultsTab).includes(currentTab)) {
			searchResultsStorageManager.setSearchTabKey(currentTab);
		}
	}, [currentTab]);

	const eventPayload = useMemo(() => {
		let paginationOffset: number;
		if (currentTab === SearchResultsTab.PRODUCTS) {
			paginationOffset = productsPagination.offset;
		} else if (currentTab === SearchResultsTab.MINDITEMS) {
			paginationOffset = mindItemsPagination.offset;
		} else {
			paginationOffset = videoMindItemsPagination.offset;
		}

		return {
			other: {
				tab: currentTab,
				results: paginationOffset,
				query: match.location.query,
				term: requestFilters.term,
			},
		};
	}, [
		currentTab,
		mindItemsPagination.offset,
		productsPagination.offset,
		videoMindItemsPagination.offset,
		match.location.query,
		requestFilters.term,
	]);

	// One AbortController per "search session" (a single filter+tab combination). The main
	// search effect aborts it and creates a fresh one before each dispatch; load-more re-uses
	// the current controller's signal so paginated requests within a session aren't cancelled
	// by each other, only by a session change (filter change, tab switch, or unmount).
	const abortControllerRef = useRef<AbortController | null>(null);

	// Tracks the tab the current controller was last dispatched for. When we abort that
	// controller without the request having succeeded, we use this to remove the tab from
	// searchedTabsRef so switching back to it re-fetches — otherwise the swallowed
	// AbortError leaves it marked "already searched" and stuck on stale LOADING state.
	const inFlightTabRef = useRef<SearchResultsTab | null>(null);

	const mindItemsLoading = useSelector(isMindItemPageLoading);
	const videoMindItemsLoading = useSelector(isVideoMindItemPageLoading);
	const productsLoading = useSelector(isProductPageLoading);
	const unifiedLoading = useSelector(isUnifiedPageLoading);

	const executeMindItemsSearch = useCallback(
		(options: SearchOptions) => {
			searchMindItems(
				requestFilters,
				{
					...options,
					populateProducts: true,
				},
				abortControllerRef.current?.signal
			);
			services.track.eventTrack(trackingEvents.searchExecute, _.merge(eventPayload));
		},
		[eventPayload, requestFilters, searchMindItems]
	);

	const executeProductsSearch = useCallback(
		(options: SearchOptions) => {
			searchProducts(requestFilters, options, abortControllerRef.current?.signal);
			services.track.eventTrack(trackingEvents.searchExecute, _.merge(eventPayload));
		},
		[requestFilters, searchProducts, eventPayload]
	);

	const executeUnifiedSearch = useCallback(
		(options: SearchOptions) => {
			searchUnified(requestFilters, options, abortControllerRef.current?.signal);
			services.track.eventTrack(trackingEvents.searchExecute, _.merge(eventPayload));
		},
		[requestFilters, searchUnified, eventPayload]
	);

	const executeVideoMindItemsSearch = useCallback(
		(options: SearchOptions) => {
			searchVideoMindItems(
				requestFilters,
				{
					...options,
					populateProducts: true,
				},
				abortControllerRef.current?.signal
			);
			services.track.eventTrack(trackingEvents.searchExecute, _.merge(eventPayload));
		},
		[requestFilters, searchVideoMindItems, eventPayload]
	);

	const updateRecentSearchSuggestions = useCallback(
		(searchQuery: SearchFilters) => {
			if (!searchQuery.term || !userId) return;
			const recentSearches = getRecentSearches(userId);
			const filteredSearches = recentSearches.filter((search) => search.term !== searchQuery.term);
			filteredSearches.unshift(searchQuery);
			filteredSearches.splice(10);
			setRecentSearches(userId, filteredSearches);
		},
		[userId]
	);

	const haveSearchFiltersChanged = useCallback(
		(filters?: SearchFilters) => {
			return !_.isEqual(filters, requestFilters);
		},
		[requestFilters]
	);

	// Tracks which tabs we've already fetched for the current `requestFilters`. Each filter
	// change clears this; each tab fires its own search lazily on first activation. Replaces the
	// legacy fan-out where every filter change fired 3 (or 4 for core members) backend searches in
	// parallel even though the user only ever sees one tab at a time.
	const searchedTabsRef = useRef<Set<SearchResultsTab>>(new Set());

	const triggerSearchForTab = useCallback(
		(tab: SearchResultsTab, options: SearchOptions) => {
			const optionsWithExperiments = {
				...options,
				experiments: searchExperiments,
				useAiFilters,
			};
			let dispatched = false;
			switch (tab) {
				case SearchResultsTab.MINDITEMS:
					executeMindItemsSearch(optionsWithExperiments);
					dispatched = true;
					break;
				case SearchResultsTab.VIDEOS:
					executeVideoMindItemsSearch(optionsWithExperiments);
					dispatched = true;
					break;
				case SearchResultsTab.PRODUCTS:
					executeProductsSearch(optionsWithExperiments);
					dispatched = true;
					break;
				case SearchResultsTab.ALL:
					if (user?.isCoreMember) {
						executeUnifiedSearch(optionsWithExperiments);
						dispatched = true;
					}
					break;
			}
			// Only mark the tab as searched if we actually dispatched. Belt-and-braces guard against
			// a future tab landing here without an associated execute*Search call — otherwise the
			// effect would treat it as fetched and never retry.
			if (dispatched) {
				searchedTabsRef.current.add(tab);
			}
		},
		[
			executeMindItemsSearch,
			executeVideoMindItemsSearch,
			executeProductsSearch,
			executeUnifiedSearch,
			user?.isCoreMember,
			searchExperiments,
			useAiFilters,
		]
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		// Wait for the restore effect to finish before triggering searches.
		if (!didRestoreFilters) return;
		if (!isDemoteReadOnlyReady) return;
		if (!isBestMatchDeepLinkReady) return;
		if (!isRelatedTopicsReady) return;
		if (!isUseAiFiltersReady) return;

		const options: SearchOptions = {
			clearPreviousResults: true,
			populateProducts: true,
		};
		const storageSessionSearchFilters = searchResultsStorageManager.getSearchFilters();

		if (haveSearchFiltersChanged(storageSessionSearchFilters)) {
			searchResultsStorageManager.clearSearchState(true);
		}

		const filtersChanged = haveSearchFiltersChanged(lastSearchFilters);

		const willDispatch = filtersChanged || !searchedTabsRef.current.has(currentTab);
		if (willDispatch) {
			// If the controller we're about to abort was tracking a still-loading tab, that
			// request will never produce a SUCCESS (the swallowed AbortError doesn't dispatch).
			// Remove that tab from searchedTabsRef so the user switching back re-triggers it.
			const inFlightTab = inFlightTabRef.current;
			const inFlightTabIsLoading =
				(inFlightTab === SearchResultsTab.MINDITEMS && mindItemsLoading) ||
				(inFlightTab === SearchResultsTab.VIDEOS && videoMindItemsLoading) ||
				(inFlightTab === SearchResultsTab.PRODUCTS && productsLoading) ||
				(inFlightTab === SearchResultsTab.ALL && unifiedLoading);
			if (inFlightTab && inFlightTabIsLoading) {
				searchedTabsRef.current.delete(inFlightTab);
			}
			abortControllerRef.current?.abort();
			abortControllerRef.current = new AbortController();
		}

		if (filtersChanged) {
			mainContentContainer?.scrollTo(0, 0);
			searchedTabsRef.current = new Set();
			triggerSearchForTab(currentTab, options);
			inFlightTabRef.current = currentTab;
		} else if (!searchedTabsRef.current.has(currentTab)) {
			triggerSearchForTab(currentTab, options);
			inFlightTabRef.current = currentTab;
		}

		// Tie the subject-topics fetch and the recent-search write to actual search
		// dispatches. Without this guard, unrelated effect re-runs (loading transitions,
		// triggerSearchForTab churn) would redundantly fire these on every render.
		if (willDispatch) {
			if (requestFilters.subject) {
				// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
				requestFilters.subject.forEach((subject: string) => searchTopics(subject));
			}
			if (isRelatedTopicsTreatment && requestFilters.term) {
				// A term not reached via a chip click (fresh search, recent search,
				// deep link) starts a new drill-down, so clear the breadcrumb trail.
				if (requestFilters.term !== lastChipTermRef.current) {
					breadcrumbTrailRef.current = [];
				}
				const excludeTopics =
					breadcrumbTrailRef.current.length > 0 ? breadcrumbTrailRef.current : undefined;
				fetchRelatedTopics(
					{
						term: requestFilters.term,
						subject: requestFilters.subject,
						classYear: requestFilters.classYear,
						schoolType: requestFilters.schoolType,
						excludeTopics,
					},
					abortControllerRef.current?.signal
				);
			}
			updateRecentSearchSuggestions(match.location.query);
		}
	}, [
		isDemoteReadOnlyReady,
		isBestMatchDeepLinkReady,
		isRelatedTopicsReady,
		isUseAiFiltersReady,
		match.location.search,
		match.location.pathname,
		match.location.query,
		requestFilters,
		lastSearchFilters,
		router,
		searchTopics,
		fetchRelatedTopics,
		isRelatedTopicsTreatment,
		triggerSearchForTab,
		updateRecentSearchSuggestions,
		haveSearchFiltersChanged,
		mainContentContainer,
		didRestoreFilters,
		currentTab,
		mindItemsLoading,
		videoMindItemsLoading,
		productsLoading,
		unifiedLoading,
	]);

	// Cancel any in-flight search when the page unmounts so the API socket is freed
	// immediately instead of being held until the 5s opensearch-js timeout.
	useEffect(() => () => abortControllerRef.current?.abort(), []);

	const changeTab = (key: SearchResultsTab) => {
		router.push({
			...match.location,
			pathname: `/${routes.search.root}/${key}`,
		});
	};

	const containerSx = {
		maxWidth: cardDimensions.desktop.maxWidth,
		marginX: 'auto',
		paddingX: 0,
		width: '100%',
	};
	const getContainerTabs = (): SearchResultsItemsTab[] => {
		const tabs: SearchResultsItemsTab[] = [
			{
				key: SearchResultsTab.MINDITEMS,
				name: t('Worksheets'),
				icon: 'mindItemTab',
				loadMore: () =>
					executeMindItemsSearch({
						clearPreviousResults: false,
						experiments: searchExperiments,
						useAiFilters,
					}),
			},
			{
				key: SearchResultsTab.PRODUCTS,
				name: t('E-books'),
				icon: 'productTab',
				loadMore: () =>
					executeProductsSearch({
						clearPreviousResults: false,
						experiments: searchExperiments,
						useAiFilters,
					}),
			},
			{
				key: SearchResultsTab.VIDEOS,
				name: t('Videos'),
				icon: 'videoTab',
				loadMore: () =>
					executeVideoMindItemsSearch({
						clearPreviousResults: false,
						experiments: searchExperiments,
						useAiFilters,
					}),
			},
		];

		if (user?.isCoreMember) {
			tabs.unshift({
				key: SearchResultsTab.ALL,
				name: t('All'),
				icon: 'alleTab',
				loadMore: () =>
					executeUnifiedSearch({
						clearPreviousResults: false,
						experiments: searchExperiments,
						useAiFilters,
					}),
			});
		}

		return tabs;
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	const memoizedNotification = useMemo(() => {
		if (!notification) return null;
		return (
			<Box sx={{ position: 'sticky', top: 0, zIndex: zIndex.alert, width: '100%' }}>
				<Box sx={{ position: 'relative', width: '100%' }}>
					<NotificationAlert
						message={notification.message}
						severity={notification.severity}
						onClose={hideNotification}
						alertSx={{
							width: '100%',
							marginLeft: `${spaces.md} !important`,
							marginRight: `${spaces.md} !important`,
							maxWidth: cardDimensions.desktop.maxWidth,
							marginX: 'auto',
						}}
					/>
				</Box>
			</Box>
		);
	}, [notification, hideNotification]);

	return (
		<MasterLayout>
			<Box sx={{ backgroundColor: palette.common.white }} data-testid="search-page">
				<SearchPageContainer>
					{memoizedNotification}
					{isRelatedTopicsTreatment && (
						<RelatedTopicsPills term={requestFilters.term} onSelect={handleRelatedTopicSelect} />
					)}
					<Box sx={containerSx}>
						<Box sx={{ paddingX: spaces.md }}>
							<PageHeader sx={{ color: palette.text.primary }} text={t('Browse all')} />
							<SearchFiltersContainer
								requestFilters={requestFilters}
								visibleKeys={visibleKeys}
								invisibleKeys={[]}
								dialogFiltersType="core"
								dialogFiltersOrder={[...searchFilterKeys]}
							/>
							<ActiveFilterChips
								requestFilters={requestFilters}
								filterKeys={[...searchFilterKeys]}
								onRemoveFilter={removeFilter}
								onResetAll={resetAllFilters}
							/>
						</Box>
					</Box>
					<SearchResults
						trackingPayload={eventPayload.other}
						requestFilters={requestFilters}
						tabs={getContainerTabs()}
						currentTab={currentTab}
						setCurrentTab={changeTab}
						searchTerm={requestFilters.term}
						onShowNotification={handleShowNotification}
						isRelatedTopicsTreatment={isRelatedTopicsTreatment}
						onRelatedTopicSelect={
							isRelatedTopicsTreatment ? (topic) => updateQueryParams({ term: topic }) : undefined
						}
					/>
				</SearchPageContainer>
			</Box>
		</MasterLayout>
	);
};

export default SearchPage;
